if (!customElements.get('proof-videos')) {
  customElements.define(
    'proof-videos',
    class ProofVideos extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-pv-dialog]');
        this.video = this.querySelector('[data-pv-video]');
        this.toggle = this.querySelector('[data-pv-toggle]');
        this.muteButton = this.querySelector('[data-pv-mute]');
        this.unmuteButton = this.querySelector('[data-pv-unmute]');
        this.detailsToggle = this.querySelector('[data-pv-details-toggle]');
        this.adTag = this.querySelector('[data-pv-ad-tag]');
        this.error = this.querySelector('[data-pv-error]');
        this.progress = this.querySelector('[data-pv-progress]');
        this.rail = this.querySelector('[data-pv-rail]');
        this.nav = this.querySelector('[data-pv-nav]');
        this.back = this.querySelector('[data-pv-back]');
        this.forward = this.querySelector('[data-pv-forward]');
        this.pauseButton = this.querySelector('[data-pv-pause]');
        this.hint = this.querySelector('[data-pv-hint]');
        this.cards = Array.from(this.querySelectorAll('[data-pv-src]'));
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.index = 0;
        this.muted = false;
        this.visible = new Set();
        this.loopsPaused = false;

        this.cards.forEach((card, index) => {
          card.addEventListener('click', () => this.open(index));
        });

        this.bindRow();

        // With every video hidden the row is only written reviews, and there is
        // no player to bind.
        if (!this.dialog) return;

        this.bindPlayer();
        this.bindLoops();
      }

      disconnectedCallback() {
        if (this.dialog && this.dialog.open) this.dialog.close();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.loopObserver) this.loopObserver.disconnect();
      }

      bindPlayer() {
        const { dialog, video, toggle } = this;

        this.querySelector('[data-pv-close]').addEventListener('click', () => dialog.close());
        this.muteButton.addEventListener('click', () => this.setMuted(!this.muted));
        this.unmuteButton.addEventListener('click', () => this.setMuted(false));
        this.querySelector('[data-pv-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-pv-next]')?.addEventListener('click', () => this.step(1));
        this.detailsToggle.addEventListener('click', () => {
          this.setDetails(!dialog.classList.contains('is-details'));
        });

        // A short press pauses; a swipe up or down moves between videos. With the
        // details sheet open, a press on the video closes the sheet instead.
        let startX = 0;
        let startY = 0;
        let swiped = false;

        toggle.addEventListener('pointerdown', (event) => {
          startX = event.clientX;
          startY = event.clientY;
          swiped = false;
        });

        toggle.addEventListener('pointerup', (event) => {
          const moveX = event.clientX - startX;
          const moveY = event.clientY - startY;
          if (this.cards.length > 1 && Math.abs(moveY) > 56 && Math.abs(moveY) > Math.abs(moveX)) {
            swiped = true;
            this.step(moveY < 0 ? 1 : -1);
          }
        });

        toggle.addEventListener('click', () => {
          if (swiped) {
            swiped = false;
            return;
          }
          if (dialog.classList.contains('is-details')) {
            this.setDetails(false);
            return;
          }
          this.togglePlay();
        });

        dialog.addEventListener('click', (event) => {
          if (event.target === dialog || event.target.hasAttribute('data-pv-theatre')) dialog.close();
        });

        dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          } else if (event.key === 'm' || event.key === 'M') {
            this.setMuted(!this.muted);
          }
        });

        dialog.addEventListener('close', () => {
          this.hideHint();
          video.pause();
          video.removeAttribute('src');
          video.load();
          this.setDetails(false);
          document.body.classList.remove('overflow-hidden');
          this.syncLoops();
        });

        video.addEventListener('play', () => this.syncPlaying());
        video.addEventListener('pause', () => this.syncPlaying());
        video.addEventListener('playing', () => dialog.classList.remove('is-loading'));
        video.addEventListener('canplay', () => dialog.classList.remove('is-loading'));
        video.addEventListener('waiting', () => dialog.classList.add('is-loading'));

        video.addEventListener('timeupdate', () => {
          const share = video.duration ? video.currentTime / video.duration : 0;
          this.progress.style.transform = `scaleX(${share})`;
        });

        video.addEventListener('error', () => {
          if (!video.getAttribute('src')) return;
          dialog.classList.remove('is-loading');
          this.error.hidden = false;
        });

        // Runs through the set once, and stops on the last clip with the button
        // beside it. Someone who has asked for less motion chooses each clip.
        video.addEventListener('ended', () => {
          if (this.reduceMotion.matches || this.index === this.cards.length - 1) return;
          this.step(1);
        });

        // The main buy form is outside the dialog, so it has to close first for
        // whatever that form does next to be seen.
        this.querySelectorAll('[data-pv-buy]').forEach((buy) => {
          buy.addEventListener('click', (event) => {
            const form = document.querySelector('product-form form');
            if (!form || new URL(buy.href).pathname !== window.location.pathname) return;
            event.preventDefault();
            dialog.close();
            form.requestSubmit();
          });
        });
      }

      bindRow() {
        const { rail, nav, back, forward } = this;

        const update = () => {
          nav.hidden = rail.scrollWidth - rail.clientWidth <= 1;
          back.disabled = rail.scrollLeft <= 1;
          forward.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
        };

        const move = (direction) => {
          const distance = rail.firstElementChild.offsetWidth + parseFloat(getComputedStyle(rail).columnGap);
          rail.scrollBy({
            left: direction * distance * 3,
            behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
          });
        };

        back.addEventListener('click', () => move(-1));
        forward.addEventListener('click', () => move(1));
        rail.addEventListener('scroll', update, { passive: true });

        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(rail);
      }

      // Cards loop a few silent seconds while they are on screen. Nothing loads
      // before the row is reached, and a card scrolled away stops decoding.
      bindLoops() {
        if (this.reduceMotion.matches || navigator.connection?.saveData) return;

        this.pauseButton.hidden = false;
        this.pauseButton.addEventListener('click', () => {
          this.loopsPaused = !this.loopsPaused;
          this.classList.toggle('is-still', this.loopsPaused);
          this.pauseButton.setAttribute('aria-label', this.loopsPaused ? 'Play the videos' : 'Pause the videos');
          this.syncLoops();
        });

        this.loopObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(({ target, isIntersecting }) => {
              if (isIntersecting) this.visible.add(target);
              else this.visible.delete(target);
            });
            this.syncLoops();
          },
          { threshold: 0.1 }
        );

        this.cards.forEach((card) => this.loopObserver.observe(card));
      }

      syncLoops() {
        this.cards.forEach((card) => {
          const run = this.visible.has(card) && !this.loopsPaused && !this.dialog.open;
          const loop = card.querySelector('.pv__loop') || (run ? this.addLoop(card) : null);
          if (!loop) return;
          if (run) loop.play().catch(() => {});
          else loop.pause();
        });
      }

      addLoop(card) {
        const loop = document.createElement('video');
        loop.className = 'pv__loop';
        loop.muted = true;
        loop.loop = true;
        loop.playsInline = true;
        loop.setAttribute('aria-hidden', 'true');

        // Without a short loop file this is the whole video, so it's held to
        // the opening seconds here.
        loop.addEventListener('timeupdate', () => {
          if (loop.currentTime >= 3) loop.currentTime = 0;
        });
        loop.addEventListener('playing', () => loop.classList.add('is-playing'), { once: true });

        if (card.dataset.pvLoopWhole === 'true') this.fetchOpening(loop, card.dataset.pvLoop);
        else loop.src = card.dataset.pvLoop;

        const poster = card.querySelector('.pv__poster');
        if (poster) poster.after(loop);
        else card.prepend(loop);
        return loop;
      }

      // Left to itself a video element buffers far past the 3 seconds a card
      // shows, so a long clip would cost its whole file. Taking the opening
      // bytes caps that. Shopify's MP4s keep their index at the front, so the
      // cut file still plays. If this fails, the card keeps its poster.
      async fetchOpening(loop, src) {
        try {
          const response = await fetch(src, { headers: { Range: 'bytes=0-2399999' } });
          if (!response.ok) return;
          const opening = await response.blob();
          loop.src = URL.createObjectURL(new Blob([opening], { type: 'video/mp4' }));
          // If a burst of detail runs the bytes out early, go round again.
          loop.addEventListener('waiting', () => {
            if (loop.currentTime > 1) loop.currentTime = 0;
          });
          this.syncLoops();
        } catch (error) {
          loop.remove();
        }
      }

      open(index) {
        document.body.classList.add('overflow-hidden');
        this.dialog.showModal();
        this.syncLoops();
        this.load(index);
        this.showHint();
      }

      step(direction) {
        const total = this.cards.length;
        this.hideHint();
        this.load((this.index + direction + total) % total);
      }

      // Once a visit, on a touch screen where the player fills it: nothing else
      // says that a swipe up moves on. It goes at the first move, or after 3 s.
      showHint() {
        const { hint } = this;
        if (!hint || this.hintShown || !window.matchMedia('(max-width: 989px) and (pointer: coarse)').matches) return;
        this.hintShown = true;
        try {
          if (sessionStorage.getItem('pv-swipe-hint')) return;
          sessionStorage.setItem('pv-swipe-hint', '1');
        } catch (error) {
          // Private modes can refuse storage; showing it once per page is fine.
        }
        hint.classList.remove('is-gone');
        hint.hidden = false;
        this.hintTimer = setTimeout(() => this.hideHint(), 3000);
      }

      hideHint() {
        const { hint } = this;
        if (!hint || hint.hidden) return;
        clearTimeout(this.hintTimer);
        hint.classList.add('is-gone');
        setTimeout(() => {
          hint.hidden = true;
        }, 300);
      }

      load(index) {
        const { dialog, video } = this;
        const clip = this.cards[index].dataset;
        this.index = index;

        dialog.setAttribute('aria-label', clip.pvHook || 'Video');
        this.querySelector('[data-pv-words-text]').textContent = clip.pvWords || clip.pvHook;
        this.querySelector('[data-pv-name-text]').textContent = clip.pvName;
        const count = this.querySelector('[data-pv-count]');
        if (count) count.textContent = `${index + 1} / ${this.cards.length}`;

        this.adTag.hidden = clip.pvAd !== 'true';
        this.error.hidden = true;
        this.progress.style.transform = 'scaleX(0)';
        dialog.classList.add('is-loading');
        video.poster = clip.pvPoster;
        video.src = clip.pvSrc;
        video.muted = this.muted;
        this.play();
      }

      play() {
        const { video } = this;
        video.play().catch((error) => {
          if (error.name !== 'NotAllowedError' || video.muted) return;
          // The browser refused sound without a press, so carry on silently and
          // offer it over the video.
          this.setMuted(true);
          this.unmuteButton.hidden = false;
          video.play().catch(() => {});
        });
      }

      togglePlay() {
        if (this.video.paused) this.play();
        else this.video.pause();
      }

      setMuted(muted) {
        this.muted = muted;
        this.video.muted = muted;
        this.dialog.classList.toggle('is-muted', muted);
        this.muteButton.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        this.unmuteButton.hidden = true;
      }

      setDetails(open) {
        this.dialog.classList.toggle('is-details', open);
        this.detailsToggle.setAttribute('aria-expanded', String(open));
        this.detailsToggle.setAttribute(
          'aria-label',
          open ? 'Hide product details' : 'Show product details and the words in this video'
        );
      }

      syncPlaying() {
        const paused = this.video.paused;
        this.dialog.classList.toggle('is-paused', paused);
        this.toggle.setAttribute('aria-label', paused ? 'Play' : 'Pause');
      }
    }
  );
}
